import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { OrgSignInLink } from '@/components/public/org/OrgSignInLink';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Package } from 'lucide-react';
import { isMemberPortalInventoryHireVisible, type InventoryHireStatus } from '@court-sync/shared';
import { Button, Card, CardContent } from '@/components/ui';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { CourtHireReceiptLink } from '@/components/member/CourtHireReceiptLink';
import { useOrgMemberStore } from '@/store/orgMember';
import { memberPortalService, type MemberInventoryHireRow } from '@/services/memberPortal';
import { formatCurrency, type Currency } from '@/utils/currency';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { useUnifiedConsumerNav } from '@/hooks/useUnifiedConsumerNav';
import { useAuthStore } from '@/store/auth';
import { hasActiveCourtHireCheckoutSession } from '@/lib/courtHireCheckoutSession';

function formatCents(cents: number, currency: string): string {
  return formatCurrency(cents / 100, currency as Currency);
}

function groupEquipmentByBooking(hires: MemberInventoryHireRow[]) {
  const byBooking = new Map<string, MemberInventoryHireRow[]>();
  const standalone: MemberInventoryHireRow[] = [];

  for (const hire of hires) {
    if (hire.courtHireBookingId) {
      const list = byBooking.get(hire.courtHireBookingId) ?? [];
      list.push(hire);
      byBooking.set(hire.courtHireBookingId, list);
    } else {
      standalone.push(hire);
    }
  }

  return { byBooking, standalone };
}

export function CourtHireMyBookingsTab() {
  const clubSlug = useOrgMemberStore((s) => s.clubSlug);
  const { user } = useAuthStore();
  const consumerNav = useUnifiedConsumerNav();
  const { p } = useOrgPaths();
  const queryClient = useQueryClient();
  const bookPath = p('/court-hire');

  useEffect(() => {
    if (!clubSlug || hasActiveCourtHireCheckoutSession(clubSlug)) return;
    void memberPortalService.cancelPendingCourtHireBookings(clubSlug).then(() => {
      void queryClient.invalidateQueries({ queryKey: ['member-court-hire-bookings', clubSlug] });
      void queryClient.invalidateQueries({ queryKey: ['member-inventory-hires', clubSlug] });
      void queryClient.invalidateQueries({ queryKey: ['org-consumer-inventory-hires'] });
    }).catch(() => undefined);
  }, [clubSlug, queryClient]);

  const bookingsQuery = useQuery({
    queryKey: ['member-court-hire-bookings', clubSlug],
    enabled: Boolean(clubSlug) && Boolean(user) && consumerNav.hasClubMemberAccess,
    queryFn: async () => {
      if (!clubSlug) return [];
      const res = await memberPortalService.getMyCourtHireBookings(clubSlug);
      return res.success && res.data ? res.data : [];
    },
  });

  const hiresQuery = useQuery({
    queryKey: ['member-inventory-hires', clubSlug],
    enabled: Boolean(clubSlug) && Boolean(user) && consumerNav.hasClubMemberAccess,
    queryFn: async () => {
      if (!clubSlug) return [];
      const res = await memberPortalService.getMyInventoryHires(clubSlug);
      return res.success && res.data ? res.data : [];
    },
  });

  const visibleHires = useMemo(
    () =>
      (hiresQuery.data ?? []).filter((hire) =>
        isMemberPortalInventoryHireVisible(hire.status as InventoryHireStatus)
      ),
    [hiresQuery.data]
  );

  const { byBooking, standalone } = useMemo(
    () => groupEquipmentByBooking(visibleHires),
    [visibleHires]
  );

  if (!user) {
    return (
      <DashboardEmptyStateCard>
        <DashboardEmptyStateIcon icon={ClipboardList} />
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">Sign in to view bookings</h3>
        <p className="mb-4 text-muted-foreground">
          Your court and equipment reservations appear here after you sign in.
        </p>
        <OrgSignInLink className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground no-underline hover:bg-primary/90">
          Sign in
        </OrgSignInLink>
      </DashboardEmptyStateCard>
    );
  }

  if (!consumerNav.hasClubMemberAccess || !consumerNav.hasCourtHire) {
    return (
      <DashboardEmptyStateCard>
        <DashboardEmptyStateIcon icon={ClipboardList} />
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">Club access required</h3>
        <p className="text-muted-foreground">
          Court bookings are available to club members. Join or sign in with your member account to continue.
        </p>
      </DashboardEmptyStateCard>
    );
  }

  const isLoading = bookingsQuery.isLoading || hiresQuery.isLoading;
  const bookings = bookingsQuery.data ?? [];
  const hasContent = bookings.length > 0 || visibleHires.length > 0;

  if (isLoading) {
    return <p className="text-muted-foreground">Loading your bookings…</p>;
  }

  if (!hasContent) {
    return (
      <DashboardEmptyStateCard>
        <DashboardEmptyStateIcon icon={ClipboardList} />
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">No court bookings yet</h3>
        <p className="mb-4 text-muted-foreground">
          Book a court online. Equipment you add at checkout will appear here too.
        </p>
        {bookPath ? (
          <Button asChild>
            <Link to={bookPath}>Book a court</Link>
          </Button>
        ) : null}
      </DashboardEmptyStateCard>
    );
  }

  return (
    <div className="space-y-8">
      {bookings.length > 0 ? (
        <section aria-labelledby="court-bookings-heading">
          <h2 id="court-bookings-heading" className="mb-3 text-lg font-semibold text-foreground">
            Court bookings
          </h2>
          <div className="space-y-3">
            {bookings.map((booking) => {
              const equipment = byBooking.get(booking.id) ?? [];
              return (
                <Card key={booking.id}>
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-medium">{booking.courtName ?? 'Court'}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(booking.startAt).toLocaleString()} –{' '}
                          {new Date(booking.endAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium capitalize">{booking.status}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatCents(booking.priceCents, booking.currency)}
                        </p>
                        {clubSlug ? (
                          <CourtHireReceiptLink
                            clubSlug={clubSlug}
                            bookingId={booking.id}
                            hasReceipt={booking.hasReceipt}
                            className="mt-1 justify-end"
                          />
                        ) : null}
                      </div>
                    </div>
                    {equipment.length > 0 ? (
                      <ul className="mt-3 space-y-1 border-t border-border pt-3 text-sm text-muted-foreground">
                        {equipment.map((hire) => (
                          <li key={hire.id} className="flex items-center gap-2">
                            <Package className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            <span>
                              {hire.itemName}
                              {hire.quantity > 1 ? ` × ${hire.quantity}` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      ) : null}

      {standalone.length > 0 ? (
        <section aria-labelledby="equipment-hires-heading">
          <h2 id="equipment-hires-heading" className="mb-3 text-lg font-semibold text-foreground">
            Equipment hires
          </h2>
          <div className="space-y-3">
            {standalone.map((hire) => (
              <Card key={hire.id}>
                <CardContent className="p-4">
                  <p className="font-medium">{hire.itemName}</p>
                  <p className="text-sm text-muted-foreground">
                    {hire.quantity} × {new Date(hire.startAt).toLocaleString()} –{' '}
                    {new Date(hire.endAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  <p className="mt-1 text-sm capitalize">{hire.status}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
